// ============================================================================
// BASE ENDPOINTS
// ============================================================================
// Base endpoint utilities and helpers

import { apiConfig } from '../config/apiConfig';

/**
 * Get API version prefix
 */
export const getVersionPrefix = (): string => {
  return apiConfig.apiVersion ? `/${apiConfig.apiVersion}` : '';
};

/**
 * Build endpoint URL with version
 */
export const buildEndpoint = (path: string): string => {
  const versionPrefix = getVersionPrefix();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${versionPrefix}${cleanPath}`;
};

/**
 * Build endpoint with path parameters
 * @example buildEndpointWithParams('/users/:id', { id: 123 }) => '/users/123'
 */
export const buildEndpointWithParams = (
  path: string,
  params: Record<string, string | number>
): string => {
  let endpoint = path;

  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`:${key}`, String(value));
  });

  return buildEndpoint(endpoint);
};

/**
 * Build endpoint with query parameters.
 *
 * Arrays expand to repeated keys so ASP.NET Core's model binder maps them to
 * `List<T>` correctly (e.g. `{ codes: ['A','B'] }` → `?codes=A&codes=B`,
 * NOT `?codes=A,B` — the comma form binds to a single-element list `["A,B"]`).
 *
 * @example buildEndpointWithQuery('/users', { page: 1, limit: 10 }) => '/users?page=1&limit=10'
 * @example buildEndpointWithQuery('/users', { roles: ['Admin','Manager'] }) => '/users?roles=Admin&roles=Manager'
 */
export const buildEndpointWithQuery = (
  path: string,
  query: Record<string, any>
): string => {
  const endpoint = buildEndpoint(path);
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== '') {
          params.append(key, String(v));
        }
      });
    } else {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `${endpoint}?${queryString}` : endpoint;
};

/**
 * Common endpoint patterns
 */
export const ENDPOINT_PATTERNS = {
  LIST: '',
  DETAIL: '/:id',
  CREATE: '',
  UPDATE: '/:id',
  DELETE: '/:id',
  SEARCH: '/search',
  BULK: '/bulk',
  EXPORT: '/export',
  IMPORT: '/import',
} as const;
